import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension, InputRule, textblockTypeInputRule } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import HardBreak from '@tiptap/extension-hard-break';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import TextAlign from '@tiptap/extension-text-align';
import { uploadImageFile } from '../../utils/api';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaCode,
  FaListUl,
  FaListOl,
  FaCheckSquare,
  FaQuoteLeft,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaLink,
  FaUnlink,
  FaImage,
  FaMinus,
  FaUndo,
  FaRedo,
  FaTimes,
  FaCheck,
} from 'react-icons/fa';

// Extension 1: When Enter is pressed at the end of a heading, naturally create a paragraph on the next line
const SmartHeadingEnter = Extension.create({
  name: 'smartHeadingEnter',
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from, empty } = selection;
        const currentBlock = $from.parent;

        if (
          empty &&
          currentBlock.type.name === 'heading' &&
          $from.parentOffset === currentBlock.content.size
        ) {
          return editor.chain().splitBlock().setParagraph().run();
        }
        return false;
      },
    };
  },
});

// Extension 2: When space is typed at the end of a link, automatically exit link mode so subsequent words are unlinked
const LinkExitOnSpace = Extension.create({
  name: 'linkExitOnSpace',
  addKeyboardShortcuts() {
    return {
      ' ': ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from, empty } = selection;

        if (empty && editor.isActive('link')) {
          const nodeBefore = $from.nodeBefore;
          const nodeAfter = $from.nodeAfter;

          const beforeHasLink = nodeBefore && nodeBefore.marks.some((m) => m.type.name === 'link');
          const afterHasLink = nodeAfter && nodeAfter.marks.some((m) => m.type.name === 'link');

          // Cursor is at the end boundary of link text
          if (beforeHasLink && !afterHasLink) {
            return editor.chain().insertContent(' ').unsetLink().run();
          }
        }
        return false;
      },
    };
  },
});

// Extension 3: Explicit Markdown Heading Shortcuts (# -> H1, ## -> H2, ### -> H3)
const CustomMarkdownRules = Extension.create({
  name: 'customMarkdownRules',
  addInputRules() {
    return [
      // # followed by space at start of line -> H1
      textblockTypeInputRule({
        find: /^#\s$/,
        type: this.editor.schema.nodes.heading,
        getAttributes: { level: 1 },
      }),
      // ## followed by space at start of line -> H2
      textblockTypeInputRule({
        find: /^##\s$/,
        type: this.editor.schema.nodes.heading,
        getAttributes: { level: 2 },
      }),
      // ### followed by space at start of line -> H3
      textblockTypeInputRule({
        find: /^###\s$/,
        type: this.editor.schema.nodes.heading,
        getAttributes: { level: 3 },
      }),
      // Also support: #Word followed by space at start of line (e.g. #Heading -> H1 "Heading")
      new InputRule({
        find: /^(#{1,3})([^\s#]+)\s$/,
        handler: ({ state, range, match }) => {
          const level = match[1].length;
          const text = match[2];
          const { tr } = state;
          const start = range.from;
          const end = range.to;

          tr.replaceWith(start, end, state.schema.text(text));
          tr.setBlockType(start, start + text.length, state.schema.nodes.heading, { level });
        },
      }),
    ];
  },
});

const ToolbarButton = ({ title, onClick, active = false, disabled = false, children, ariaLabel }) => (
  <button
    type="button"
    title={title}
    aria-label={ariaLabel || title}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg border text-xs transition ${
      active
        ? 'border-amber-400 bg-amber-50 text-amber-900 shadow-2xs font-semibold'
        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
    } ${disabled ? 'cursor-not-allowed opacity-35' : ''}`}
  >
    {children}
  </button>
);

const MenuBar = ({ editor, onOpenLinkModal }) => {
  const fileInputRef = useRef(null);

  const addImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file || !editor) return;

      if (file.size > 15 * 1024 * 1024) {
        alert('Image is too large. Please select an image under 15MB.');
        event.target.value = '';
        return;
      }

      try {
        const imageUrl = await uploadImageFile(file);
        editor.chain().focus().setImage({ src: imageUrl, alt: file.name }).run();
      } catch (err) {
        console.error(err);
        alert('Image upload failed. Please try again.');
      }

      event.target.value = '';
    },
    [editor]
  );

  const handleFormatChange = (e) => {
    const format = e.target.value;
    if (!editor) return;

    if (format === 'p') {
      editor.chain().focus().setParagraph().run();
    } else if (format === 'h1') {
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    } else if (format === 'h2') {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    } else if (format === 'h3') {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    } else if (format === 'quote') {
      editor.chain().focus().toggleBlockquote().run();
    } else if (format === 'code') {
      editor.chain().focus().toggleCodeBlock().run();
    }
  };

  if (!editor) {
    return null;
  }

  // Determine current active format for dropdown
  let currentFormat = 'p';
  if (editor.isActive('heading', { level: 1 })) currentFormat = 'h1';
  else if (editor.isActive('heading', { level: 2 })) currentFormat = 'h2';
  else if (editor.isActive('heading', { level: 3 })) currentFormat = 'h3';
  else if (editor.isActive('blockquote')) currentFormat = 'quote';
  else if (editor.isActive('codeBlock')) currentFormat = 'code';

  const isLinkActive = editor.isActive('link');

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 sm:gap-1.5 border-b border-slate-200 bg-slate-50/95 p-2 backdrop-blur">
      {/* 1. Format Selector Dropdown (Paragraph, H1, H2, H3, Quote, Code) */}
      <select
        value={currentFormat}
        onChange={handleFormatChange}
        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-2xs transition focus:border-amber-400 focus:outline-none"
        title="Text format"
      >
        <option value="p">Paragraph (Normal text)</option>
        <option value="h1">Heading 1 (Main Header)</option>
        <option value="h2">Heading 2 (Section Title)</option>
        <option value="h3">Heading 3 (Sub-header)</option>
        <option value="quote">Blockquote</option>
        <option value="code">Code Block</option>
      </select>

      <div className="h-4 w-px bg-slate-200 mx-0.5" />

      {/* 2. Basic Inline Formatting */}
      <ToolbarButton
        title="Bold (Ctrl+B)"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FaBold className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Italic (Ctrl+I)"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FaItalic className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Underline (Ctrl+U)"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <FaUnderline className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Strikethrough"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <FaStrikethrough className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Inline Code"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <FaCode className="text-[11px]" />
      </ToolbarButton>

      <div className="h-4 w-px bg-slate-200 mx-0.5" />

      {/* 3. Text Alignment */}
      <ToolbarButton
        title="Align Left"
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <FaAlignLeft className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Align Center"
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <FaAlignCenter className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Align Right"
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <FaAlignRight className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Justify"
        active={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      >
        <FaAlignJustify className="text-[11px]" />
      </ToolbarButton>

      <div className="h-4 w-px bg-slate-200 mx-0.5" />

      {/* 4. Lists & Quotes */}
      <ToolbarButton
        title="Bullet List"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FaListUl className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Numbered List"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FaListOl className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Task Checklist"
        active={editor.isActive('taskList')}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <FaCheckSquare className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Blockquote"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <FaQuoteLeft className="text-[11px]" />
      </ToolbarButton>

      <div className="h-4 w-px bg-slate-200 mx-0.5" />

      {/* 5. Link Tools */}
      <ToolbarButton
        title={isLinkActive ? 'Edit Link' : 'Insert Link'}
        active={isLinkActive}
        onClick={onOpenLinkModal}
      >
        <FaLink className="text-[11px]" />
      </ToolbarButton>

      {isLinkActive && (
        <button
          type="button"
          onClick={handleRemoveLink}
          title="Remove Link (Unlink)"
          className="inline-flex h-7 items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 text-xs font-semibold text-red-600 shadow-2xs hover:bg-red-100 transition"
        >
          <FaUnlink className="text-[10px]" />
          <span className="hidden sm:inline">Unlink</span>
        </button>
      )}

      {/* 6. Media & Dividers */}
      <ToolbarButton title="Upload Image" onClick={addImage}>
        <FaImage className="text-[11px]" />
      </ToolbarButton>

      <ToolbarButton
        title="Horizontal Divider"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <FaMinus className="text-[11px]" />
      </ToolbarButton>

      {/* 7. History (Undo / Redo) */}
      <div className="ml-auto flex items-center gap-1">
        <ToolbarButton
          title="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <FaUndo className="text-[10px]" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <FaRedo className="text-[10px]" />
        </ToolbarButton>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
    </div>
  );
};

export default function RichTextEditor({ value, onChange }) {
  const [, setEditorStateVersion] = useState(0);

  // Link Modal State
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkOpenInNewTab, setLinkOpenInNewTab] = useState(true);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      CustomMarkdownRules,
      SmartHeadingEnter,
      LinkExitOnSpace,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        inclusive: false, // Prevents link mark from applying to subsequent typed text
        HTMLAttributes: {
          class: 'text-amber-600 underline font-medium hover:text-amber-700 cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image,
      HardBreak,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'editor-content prose prose-sm max-w-none min-h-[260px] px-4 py-3 text-sm leading-relaxed text-slate-800 focus:outline-none prose-p:my-1.5 prose-headings:mt-3 prose-headings:mb-1',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    const updateHandler = () => setEditorStateVersion((state) => state + 1);

    editor.on('selectionUpdate', updateHandler);
    editor.on('transaction', updateHandler);

    return () => {
      editor.off('selectionUpdate', updateHandler);
      editor.off('transaction', updateHandler);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    const currentHtml = editor.getHTML();
    if (value !== currentHtml) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]);

  // Handle Opening Link Modal
  const handleOpenLinkModal = () => {
    if (!editor) return;
    const { state } = editor;
    const { from, to, empty } = state.selection;

    const existingHref = editor.getAttributes('link').href || '';
    const selectedText = empty ? '' : state.doc.textBetween(from, to, ' ');

    setLinkUrl(existingHref);
    setLinkText(selectedText);
    setLinkOpenInNewTab(true);
    setLinkModalOpen(true);
  };

  // Handle Saving Link from Modal
  const handleSaveLink = (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    if (!editor) return;

    let targetUrl = linkUrl.trim();
    if (!targetUrl) {
      // If URL is empty, remove link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkModalOpen(false);
      return;
    }

    // Auto prepend https:// if missing protocol
    if (!/^https?:\/\//i.test(targetUrl) && !/^mailto:/i.test(targetUrl) && !/^tel:/i.test(targetUrl)) {
      targetUrl = `https://${targetUrl}`;
    }

    const linkAttrs = {
      href: targetUrl,
      target: linkOpenInNewTab ? '_blank' : '_self',
      rel: 'noopener noreferrer',
    };

    const { state } = editor;
    const { to, empty } = state.selection;

    if (empty) {
      // If no text was selected, insert the link text (or the URL itself) followed by an unlinked space
      const textToDisplay = linkText.trim() || targetUrl;
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: textToDisplay,
          marks: [{ type: 'link', attrs: linkAttrs }],
        })
        .insertContent(' ') // Add an unlinked space after the link
        .unsetLink() // Explicitly ensure subsequent typing is unlinked
        .run();
    } else {
      // Set link on existing selection, then move cursor to end and unsetLink
      editor
        .chain()
        .focus()
        .setLink(linkAttrs)
        .setTextSelection(to)
        .unsetLink()
        .run();
    }

    setLinkModalOpen(false);
  };

  const handleModalRemoveLink = (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkModalOpen(false);
  };

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xs">
      <MenuBar editor={editor} onOpenLinkModal={handleOpenLinkModal} />
      <EditorContent editor={editor} className="editor-content bg-white" />

      {/* Editor Footer Advice */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border-t border-slate-100 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-500">
        <span className="font-medium text-slate-700">
          💡 Markdown Shortcuts: On a new line, type <code className="rounded bg-slate-200/70 px-1 py-0.5 text-slate-800"># </code> for H1, <code className="rounded bg-slate-200/70 px-1 py-0.5 text-slate-800">## </code> for H2, <code className="rounded bg-slate-200/70 px-1 py-0.5 text-slate-800">### </code> for H3, <code className="rounded bg-slate-200/70 px-1 py-0.5 text-slate-800">- </code> for Lists.
        </span>
        <span className="text-slate-400">
          (Tip: Headings trigger at the start of a new line)
        </span>
      </div>

      {/* Interactive Link Modal Dialog */}
      {linkModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs"
          onClick={(e) => {
            if (e.target === e.currentTarget) setLinkModalOpen(false);
          }}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl animate-in fade-in duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
                <FaLink className="text-amber-500" />
                <span>{editor.isActive('link') ? 'Edit Link' : 'Insert Link'}</span>
              </div>
              <button
                type="button"
                onClick={() => setLinkModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Link URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSaveLink(e);
                    }
                  }}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:border-amber-400 focus:bg-white focus:outline-none"
                  autoFocus
                  required
                />
              </div>

              {editor.state.selection.empty && !editor.isActive('link') && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Text to display
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Read full report"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSaveLink(e);
                      }
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 focus:border-amber-400 focus:bg-white focus:outline-none"
                  />
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="link-new-tab"
                  checked={linkOpenInNewTab}
                  onChange={(e) => setLinkOpenInNewTab(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
                />
                <label htmlFor="link-new-tab" className="text-xs text-slate-600 cursor-pointer">
                  Open link in new tab
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                {editor.isActive('link') && (
                  <button
                    type="button"
                    onClick={handleModalRemoveLink}
                    className="mr-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition"
                  >
                    <FaUnlink className="text-[10px]" /> Remove Link
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setLinkModalOpen(false)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveLink}
                  className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 transition"
                >
                  <FaCheck className="text-[10px]" /> Save Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
